// PR-INV-RATES MAY 11 — drawer for adding a new investor rate to a contract.
import { useEffect, useState } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/Sheet';
import { apiClient, extractErrorMessage } from '@/lib/api';

interface Investor { id: string; code: string; fullNameEn: string | null }
interface ActiveRateInfo { investorId: string; investorRateAtTarget: string | number }

export interface AddInvestorRateSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contractId: string;
  /** Active rates so the picker can warn that an existing rate will be replaced. */
  activeRates: ActiveRateInfo[];
  onSaved: () => void;
}

export function AddInvestorRateSheet({ open, onOpenChange, contractId, activeRates, onSaved }: AddInvestorRateSheetProps) {
  const [investors, setInvestors] = useState<Investor[]>([]);
  const [investorId, setInvestorId] = useState<string>('');
  const [rate, setRate] = useState<string>('');
  const [effectiveFrom, setEffectiveFrom] = useState<string>(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setInvestorId(''); setRate(''); setError(null);
    setEffectiveFrom(new Date().toISOString().slice(0, 10));
    apiClient.get<{ items: Investor[] } | Investor[]>('/api/v1/investors?pageSize=500')
      .then((r) => {
        const data = Array.isArray(r.data) ? r.data : (r.data.items ?? []);
        setInvestors(data);
      })
      .catch((e) => setError(extractErrorMessage(e)));
  }, [open]);

  const existingForPicked = activeRates.find((r) => r.investorId === investorId);

  async function save() {
    if (!investorId) { setError('Pick an investor'); return; }
    const rateNum = Number(rate);
    if (!Number.isFinite(rateNum) || rateNum < 0) { setError('Rate must be a non-negative number'); return; }
    setSaving(true); setError(null);
    try {
      await apiClient.post('/api/v1/site-contracts/' + contractId + '/investor-rates', {
        investorId,
        investorRateAtTarget: rateNum,
        effectiveFrom,
      });
      onSaved();
      onOpenChange(false);
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setSaving(false); }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Add investor rate</SheetTitle>
          <SheetDescription>
            Setting a new rate will end the current active rate (if any) one day before this effective date.
          </SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-6 space-y-4 text-sm">
          <div className="space-y-1">
            <Label>Investor *</Label>
            <select
              value={investorId}
              onChange={(e) => setInvestorId(e.target.value)}
              className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base"
            >
              <option value="">— Select investor —</option>
              {investors.map((i) => (
                <option key={i.id} value={i.id}>{i.code} · {i.fullNameEn ?? '—'}</option>
              ))}
            </select>
            {existingForPicked && (
              <p className="text-[11px] text-amber-700 dark:text-amber-400 inline-flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> Will replace existing {Number(existingForPicked.investorRateAtTarget).toFixed(2)} SAR rate.
              </p>
            )}
          </div>
          <div className="space-y-1">
            <Label>Rate (SAR at target) *</Label>
            <Input
              type="number"
              step="0.01"
              min={0}
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              className="font-mono"
              placeholder="40.00"
            />
          </div>
          <div className="space-y-1">
            <Label>Effective from *</Label>
            <Input
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
            />
          </div>
          {error && (
            <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm">
              {error}
            </div>
          )}
        </div>
        <div className="shrink-0 border-t border-border bg-muted/30 p-6 flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving || !investorId || !rate} className="gap-1">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Save rate
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
